import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMyJudgingCompetitions, useRanking } from '@/hooks/useJudging';
import { useCategories } from '@/hooks/useCategories';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Select } from '@/components/ui/Select';
import { Table, type Column } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { SubmissionThumbnail } from '@/components/submissions/SubmissionThumbnail';
import { CompetitionStatus, type SubmissionRanking } from '@/api/types';

export function JudgeRankingPage() {
  const competitions = useMyJudgingCompetitions();
  const [competitionId, setCompetitionId] = useState('');
  // Optional narrowing to one category; empty = the whole competition.
  const [categoryId, setCategoryId] = useState('');
  const categories = useCategories(competitionId || undefined);
  const ranking = useRanking(competitionId || undefined);

  // Judges only see standings once judging is finished - before that they could read off what the
  // other judges scored (the API refuses the request as well).
  const finishedCompetitions = (competitions.data?.items ?? []).filter((c) => c.status >= CompetitionStatus.Completed);

  const categoryNames = new Map((categories.data?.items ?? []).map((c) => [c.id, c.name]));

  // The API ranks the whole competition as one list (entries with equal averages share a place).
  // Within a single category the placing has to be counted among that category's entries only,
  // so the rank is recomputed here the same way.
  const allRanked = [...(ranking.data?.items ?? [])].sort((a, b) => a.rank - b.rank);
  const ranked = categoryId
    ? allRanked
        .filter((s) => s.categoryId === categoryId)
        .map((s, _index, inCategory) => ({ ...s, rank: 1 + inCategory.filter((o) => o.averageScore > s.averageScore).length }))
    : allRanked;
  // Tied entries: the jury has to decide between them, the organizer then gives the award.
  const isTied = (s: SubmissionRanking) => ranked.filter((o) => o.averageScore === s.averageScore).length > 1;

  const columns: Column<SubmissionRanking>[] = [
    {
      header: '#',
      accessor: (s) => (
        <span className="inline-flex items-center gap-2">
          {s.rank}
          {isTied(s) && <Badge tone="Judging">Izenačeno</Badge>}
        </span>
      ),
    },
    {
      header: '',
      accessor: (s) => (
        <Link to={`/submissions/${s.submissionId}`} className="block h-14 w-14 overflow-hidden rounded-photo bg-surface-3">
          <SubmissionThumbnail submissionId={s.submissionId} alt={s.title} />
        </Link>
      ),
    },
    {
      header: 'Prispevek',
      accessor: (s) => (
        <Link to={`/submissions/${s.submissionId}`} className="font-medium text-fg hover:underline">
          {s.title}
        </Link>
      ),
    },
    ...(categoryId
      ? []
      : [{ header: 'Kategorija', accessor: (s: SubmissionRanking) => categoryNames.get(s.categoryId) ?? '' }]),
    { header: 'Ocena', accessor: (s) => s.averageScore.toFixed(2) },
    { header: 'Št. ocen', accessor: (s) => s.scoreCount },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-fg">Razvrstitev</h1>
        <p className="text-sm text-fg-muted">
          Prispevki, razvrščeni po povprečni oceni za izbrano tekmovanje. Po želji izberite še kategorijo.
        </p>
      </div>

      {competitions.isLoading && <PageSpinner />}
      {competitions.isError && (
        <ErrorState message={getApiErrorMessage(competitions.error)} onRetry={() => competitions.refetch()} />
      )}
      {competitions.isSuccess && competitions.data.items.length === 0 && (
        <EmptyState title="Še niste dodeljeni kot sodnik na nobenem tekmovanju" />
      )}

      {competitions.isSuccess && competitions.data.items.length > 0 && finishedCompetitions.length === 0 && (
        <EmptyState
          title="Razvrstitev še ni na voljo"
          description="Razvrstitev je vidna, ko je ocenjevanje zaključeno, da ocene drugih sodnikov ne vplivajo na vaše."
        />
      )}

      {finishedCompetitions.length > 0 && (
        <div className="flex max-w-2xl flex-wrap gap-3">
          <div className="max-w-sm flex-1">
            <Select
              label="Tekmovanje"
              value={competitionId}
              onChange={(e) => {
                setCompetitionId(e.target.value);
                setCategoryId('');
              }}
            >
              <option value="">Izberite tekmovanje&hellip;</option>
              {finishedCompetitions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          {competitionId && (
            <div className="max-w-sm flex-1">
              <Select
                label="Kategorija (neobvezno)"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                disabled={categories.isLoading}
              >
                <option value="">Vse kategorije</option>
                {(categories.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
      )}

      {finishedCompetitions.length > 0 && !competitionId && (
        <EmptyState title="Izberite tekmovanje za ogled razvrstitve" />
      )}
      {competitionId && ranking.isLoading && <PageSpinner />}
      {competitionId && ranking.isError && (
        <ErrorState message={getApiErrorMessage(ranking.error)} onRetry={() => ranking.refetch()} />
      )}
      {competitionId && ranking.isSuccess && ranked.length === 0 && (
        <EmptyState title={categoryId ? 'V tej kategoriji še ni ocenjenih prispevkov' : 'Še ni ocenjenih prispevkov'} />
      )}
      {competitionId && ranking.isSuccess && ranked.length > 0 && (
        <Table columns={columns} rows={ranked} getRowKey={(s) => s.submissionId} />
      )}
    </div>
  );
}
